import { useState, useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { DarkModeContext } from "../context/DarkModeContext";
import { toast } from "react-toastify";
import { BACKEND_URL } from "../utils";

import { motion } from "framer-motion";
import gsap from "gsap";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { darkMode, toggleDarkMode } = useContext(DarkModeContext);

  const leftRef = useRef(null);
  const formRef = useRef(null);
  const titleRef = useRef(null);

  useEffect(() => {
    // LEFT ANIMATION
    if (leftRef.current) {
      gsap.fromTo(
        leftRef.current,
        { x: -60, opacity: 0 },
        { x: 0, opacity: 1, duration: 1.2, ease: "power3.out" }
      );
    }

    // FORM ANIMATION
    if (formRef.current) {
      gsap.fromTo(
        formRef.current,
        { y: 50, opacity: 0 },
        { y: 0, opacity: 1, duration: 1, delay: 0.2, ease: "power3.out" }
      );
    }

    // 🔥 SAFE TEXT ANIMATION (FIXED)
    const el = titleRef.current;

    if (!el || !el.textContent) return;

    const originalText = el.textContent;

    const timeout = setTimeout(() => {
      const letters = originalText.split("");

      el.innerHTML = letters
        .map((l) => `<span class="inline-block">${l}</span>`)
        .join("");

      gsap.fromTo(
        el.children,
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          stagger: 0.04,
          duration: 0.5,
          ease: "power3.out",
        }
      );
    }, 0);

    return () => {
      clearTimeout(timeout);
      if (el) el.textContent = originalText; // cleanup
    };
  }, []);

  const handleDemoLogin = (role) => {
    if (role === "Manager") {
      setEmail("manager@a.com");
      setPassword("123456");
    } else {
      setEmail("store@example.com");
      setPassword("123456");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/auth/login`, {
        email,
        password,
      });

      localStorage.setItem("token", res.data.token);
      localStorage.setItem("role", res.data.user.role);

      toast.success("Login successful!");

      setTimeout(() => {
        navigate("/dashboard");
      }, 300);
    } catch (err) {
      toast.error(err.response?.data?.msg || "Invalid credentials!");
    } finally {
      setLoading(false);
    }
  };

  const bgClass = darkMode ? "bg-gray-900" : "bg-gray-50";
  const formBgClass = darkMode ? "bg-gray-800/80" : "bg-white/80";
  const textClass = darkMode ? "text-white" : "text-gray-900";

  return (
    <div className={`flex flex-col md:flex-row h-screen overflow-hidden ${bgClass}`}>
      
      {/* LEFT */}
      <div
        ref={leftRef}
        className="hidden md:flex flex-1 items-center justify-center bg-indigo-600 overflow-hidden"
      >
        <h1
          ref={titleRef}
          className="text-5xl font-bold text-white text-center overflow-hidden"
        >
          Welcome Back
        </h1>
      </div>

      {/* RIGHT */}
      <div className="flex-1 flex justify-center items-center p-6 overflow-hidden">
        <motion.div ref={formRef} className="w-full max-w-md">
          
          <div className="flex justify-between items-center mb-6">
            <h2 className={`text-3xl font-bold ${textClass}`}>Login</h2>

            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={toggleDarkMode}
              className="px-3 py-1 bg-gray-400 text-white rounded"
            >
              {darkMode ? "Light" : "Dark"}
            </motion.button>
          </div>

          <form
            onSubmit={handleSubmit}
            className={`${formBgClass} backdrop-blur-lg p-8 rounded-xl shadow-2xl`}
          >
            <input
              type="email"
              placeholder="Email"
              className="w-full mb-4 p-3 border rounded"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />

            <div className="relative mb-4">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                className="w-full p-3 border rounded pr-10"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />

              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
              </button>
            </div>

            <motion.button
              whileTap={{ scale: 0.95 }}
              disabled={loading}
              className="w-full bg-indigo-600 text-white py-3 rounded flex justify-center items-center gap-2"
            >
              {loading ? "Logging in..." : "Get Started"}
            </motion.button>

            <div className="mt-4 flex gap-3">
              <button onClick={() => handleDemoLogin("Manager")} className="flex-1 bg-green-500 text-white py-2 rounded">
                Manager Demo
              </button>
              <button onClick={() => handleDemoLogin("Store Keeper")} className="flex-1 bg-yellow-500 text-white py-2 rounded">
                Store Keeper Demo
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default Login;